import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { useQueryParams } from '~/lib/useQuery';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useMediaPlayer } from '~/ui/video-player/useMediaPlayer';
import { VideoPlayer } from '~/ui/video-player/VideoPlayer';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { VIDEO } from '../itemIds';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import {
  MediaPlayerProvider,
  useMediaPlayerStore
} from '~/ui/video-player/MediaPlayerProvider';
import { PlayButton } from '~/ui/buttons';
import ReactDOM, { createPortal } from 'react-dom';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useCanControl } from '~/ui/video-player/useCanControl';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { useNextSequence } from '~/training/useNextSequence';
import { TrainingState, useTrainingStore } from '~/training/state';
import { useEditorStore } from '~/shell/Store';
import { EditBg } from '~/editor/buttons/EditBg';
import { getParentId } from '../helper/getParentId';
import { useSaveVideoWatchedSeconds } from '~/training/sequence/useSaveVideoWatchedSeconds';

interface VideoContainerStyleProps {
  additionalStyle?: {
    width?: string;
    height?: string;
    position?: string;
    borderRadius?: string;
  };
}

const VideoContainer = styled.div<VideoContainerStyleProps>`
  position: ${p => p?.additionalStyle?.position || 'fixed'};
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  margin: auto;
  width: ${p => p?.additionalStyle?.width || '100%'};
  height: ${p => p?.additionalStyle?.height || '100%'};
  & > .video-js {
    border-radius: ${p => p?.additionalStyle?.borderRadius || '0px'};
    & > video {
      border-radius: ${p => p?.additionalStyle?.borderRadius || '0px'};
    }
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  display: inline-block;

  .edit-bg-btn {
    margin-top: -10px;
  }
`;

export interface VideoComponentProps {
  id?: string | number;
  videoId?: string | number;
  mediaUrl: Array<{ src: string; type: string }>;
  subtitlesUrl?: string;
  autoPlay?: boolean;
  videoContainerStyles?: {
    width?: string;
    height?: string;
    position?: string;
    borderRadius?: string;
  };
  showProgress?: boolean;
  showPlayButton?: boolean;
  snapshotUrl?: string;
  /** whether the video should be isolated from the sequence in its own store */
  isolated?: boolean;
  loop?: boolean;
  replay?: boolean;
  blocking?: boolean;
  videoRecord?: { watchedSeconds?: number | null } | null;
}

export type VideoDefinition = ItemDefinition<typeof VIDEO, VideoComponentProps>;

const selector = (s: MediaPlayerStore) =>
  select(s, 'toggle', 'progress', 'status', 'restart', 'loadMedia', 'reset');

const sequenceSelector = (s: SequenceStore) =>
  select(s, 'autoContinue', 'addBlocker', 'removeBlocker');

const selectorUserSettings = (s: TrainingState) => select(s, 'autoPlay');

const VideoProgress = () => {
  const progress = useMediaPlayerStore(s => s.progress);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const progressAsNumber = parseFloat(progress);

  return (
    <x.div
      w="300px"
      h="4px"
      position="absolute"
      borderRadius="3px"
      backgroundColor="greyDark"
      opacity="0.4"
      bottom="-20px"
    >
      <x.div
        position="absolute"
        style={{
          width: `${Math.floor(progressAsNumber * 100)}%`
        }}
        borderRadius="3px"
        h="100%"
        backgroundColor="white"
        transition="width ease-in-out 0.3s"
      />
    </x.div>
  );
};

const VideoComponent = (props: RenderProps<VideoComponentProps>) => {
  const {
    id,
    mediaUrl,
    subtitlesUrl,
    autoPlay,
    videoContainerStyles,
    showProgress,
    showPlayButton,
    snapshotUrl,
    replay,
    blocking = false,
    parent
  } = props;

  const next = useNextSequence();
  const params = useQueryParams();
  const { status, toggle, progress, restart, loadMedia, reset } =
    useMediaPlayerStore(selector);

  const { autoContinue, addBlocker, removeBlocker } =
    useSequenceStore(sequenceSelector);

  const { autoPlay: autoPlayFromSetting } =
    useTrainingStore(selectorUserSettings);

  const [isPlay, setIsPlay] = useState(
    autoPlay && autoPlayFromSetting && params.get('disableAutoPlay') !== 'true'
  );
  const [isPlayed, setIsPlayed] = useState(false);

  useSaveVideoWatchedSeconds(props);

  const { editorMode, contentEditable } = useEditorStore();
  const divElRef = useRef<HTMLDivElement>(null);
  const [showPortal, setShowPortal] = useState(false);

  React.useEffect(() => {
    if (contentEditable && divElRef.current) {
      setShowPortal(true);
    }
  }, [divElRef.current, contentEditable]);

  useEffect(() => {
    if (showPlayButton) {
      setTimeout(() => {
        loadMedia(mediaUrl, subtitlesUrl, 'video', isPlay, 0, snapshotUrl);
      }, 0);
    }
  }, [isPlay]);

  useEffect(() => {
    if (!blocking) return;
    addBlocker(id);

    if (status == 'nomedia' || status == 'done') {
      removeBlocker(id);
    }
  }, [status]);

  if (!showPlayButton) {
    useMediaPlayer(mediaUrl, 'video', subtitlesUrl, isPlay, snapshotUrl);
  }

  useEffect(() => {
    if (status == 'playing') {
      setIsPlayed(true);
    }

    if (status == 'done' && (replay || (showPlayButton && !isPlayed))) {
      restart();
    }
  }, [status]);

  // useEffect(() => {
  //   if (status == 'done' && autoContinue && !blocking) {
  //       next();
  //   }
  // }, [status, autoContinue]);

  const onClick = () => {
    if (isPlayed) {
      toggle();
    } else {
      reset();
      setIsPlay(!isPlay);
    }
  };

  useGlobalMedia();
  const canControl = useCanControl();

  const isPhoneType = parent?.type === 'Phone';

  return (
    <>
      {isPhoneType && contentEditable ? (
        <VideoWrapper>
          <VideoContainer
            className="video-container"
            additionalStyle={videoContainerStyles}
            ref={divElRef}
          >
            <VideoPlayer showButtonAnimation={!showPlayButton} />
            {showProgress && <VideoProgress />}
            {showPlayButton &&
              (status == 'ready' ||
                status == 'paused' ||
                (status == 'done' &&
                  (replay || showPlayButton) &&
                  !isPlayed)) && (
                <x.div
                  position={'absolute'}
                  w={'100%'}
                  h={'100%'}
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  style={{ zIndex: 2 }}
                >
                  {canControl && (
                    <PlayButton
                      state={(status == 'done' ? 'ready' : status) as 'ready'}
                      onClick={onClick}
                      progress={progress}
                    />
                  )}
                </x.div>
              )}
          </VideoContainer>
          <EditBg
            divElRef={divElRef}
            id={id}
            type="video"
            parentId={getParentId(parent)}
            style={{
              position: 'absolute',
              zIndex: 199
            }}
          />
        </VideoWrapper>
      ) : (
        <>
          <VideoContainer
            className="video-container"
            additionalStyle={videoContainerStyles}
            ref={divElRef}
          >
            <VideoPlayer showButtonAnimation={!showPlayButton} />
            {showProgress && <VideoProgress />}
            {showPlayButton &&
              (status == 'ready' ||
                status == 'paused' ||
                (status == 'done' &&
                  (replay || showPlayButton) &&
                  !isPlayed)) && (
                <x.div
                  position={'absolute'}
                  w={'100%'}
                  h={'100%'}
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  style={{ zIndex: 2 }}
                >
                  {canControl && (
                    <PlayButton
                      state={(status == 'done' ? 'ready' : status) as 'ready'}
                      onClick={onClick}
                      progress={progress}
                    />
                  )}
                </x.div>
              )}
          </VideoContainer>
          {contentEditable && parent?.type != 'Background' && (
            <EditBg
              divElRef={divElRef}
              id={id}
              type="video"
              parentId={getParentId(parent)}
            />
          )}
          {contentEditable &&
            parent?.type == 'Background' &&
            createPortal(
              <EditBg
                divElRef={divElRef}
                id={id}
                type="video"
                parentId={getParentId(parent)}
              />,
              document.body
            )}
        </>
      )}
      {ReactDOM.createPortal(
        status != 'paused' && <MediaCaptions />,
        document.getElementById('root')!
      )}
    </>
  );
};

export class Video extends Item<typeof VIDEO, VideoComponentProps> {
  protected render({
    isolated = false,
    ...props
  }: RenderProps<VideoComponentProps>) {
    if (isolated) {
      return (
        <MediaPlayerProvider>
          <VideoComponent {...props} />
        </MediaPlayerProvider>
      );
    } else {
      return <VideoComponent {...props} />;
    }
  }
}

registry[VIDEO] = Video;
